import type {Feature, FeatureCollection, LineString, MultiLineString} from 'geojson';
import memoize from 'lodash/memoize';

import {getTrackStatsCalculator} from './TrackStatsCalculator';

// Track Stats Calculator modifies the track geometry by removing close/bad points and filtering
// out stopped time. This provides more accurate stats, but can result in non-trivial differences
// in things like calculated distance. Therefore, we return the simplified geometry along with the
// stats so that clients can reference it if needed.

export interface TrackStatsWithGeometry extends TrackStats {
    simplifiedGeometry: MultiLineString;
}

export const getTrackStats = memoize(
    async function getTrackStats(line: MultiLineString): Promise<TrackStatsWithGeometry> {
        const calculator = await getTrackStatsCalculator();
        calculator.addGeoJson(line);
        calculator.filter();

        const filteredGeoJson: FeatureCollection = calculator.getGeoJson();

        const lines: Feature<LineString>[] = filteredGeoJson.features.filter(
            (f): f is Feature<LineString> => f.geometry.type === 'LineString'
        );

        const simplifiedGeometry: MultiLineString = {
            type: 'MultiLineString',
            coordinates: lines.map((l) => l.geometry.coordinates),
        };

        return {...calculator.getJsonStats(), simplifiedGeometry};
    },
    (line) => JSON.stringify(line)
);
